/**
 * MobileShellProvider
 *
 * Detects whether the React bundle is running inside a native Capacitor
 * shell (Driver/Customer apps) and, if so, attaches a `mobile-app` class to
 * <body>. All mobile-only theme styling is scoped to that class so the
 * production WEB build at transportanything247.com remains untouched.
 *
 * Also accepts a `?mobile=1` query param so we can preview the mobile shell
 * in a desktop browser during QA without building a native binary.
 */

import { useEffect } from "react";
import { IS_CUSTOMER_APP, IS_DRIVER_APP } from "@/appConfig";
import "./mobile-shell.css";

export function shouldUseMobileShell() {
  if (IS_CUSTOMER_APP || IS_DRIVER_APP) return true;
  if (typeof window === "undefined") return false;
  // Persistent: once the body class is set (by MobileShellProvider on
  // initial mount), every subsequent call returns true even after route
  // changes drop the ?mobile=1 query string.
  if (typeof document !== "undefined" && document.body?.classList?.contains("mobile-app")) {
    return true;
  }
  // Allow opt-in via query param for desktop QA / screenshots.
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("mobile") === "1") return true;
  } catch {}
  // Capacitor injects this global at runtime even inside a native WebView.
  if (window.Capacitor?.isNativePlatform?.()) return true;
  return false;
}

export default function MobileShellProvider({ children }) {
  useEffect(() => {
    if (!shouldUseMobileShell()) return undefined;
    document.body.classList.add("mobile-app");
    // iOS status bar tint via theme-color meta
    const meta =
      document.querySelector('meta[name="theme-color"]') ||
      Object.assign(document.createElement("meta"), { name: "theme-color" });
    meta.setAttribute("content", "#0B1729");
    if (!meta.isConnected) document.head.appendChild(meta);
    return () => document.body.classList.remove("mobile-app");
  }, []);

  return (
    <>
      {children}
    </>
  );
}
